import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import { ZafaraLogoMark } from "@/components/landing/Logo";
import { BeninFlag } from "@/components/landing/BeninFlag";
import { useContent } from "@/context/ContentContext";

const COLS = [
  {
    title: "Navigation",
    links: [
      { l: "Accueil", to: "/" },
      { l: "Pourquoi Zafara", to: "/pourquoi-zafara" },
      { l: "L'application", to: "/application" },
      { l: "Le fondateur", to: "/fondateur" },
      { l: "Whilsco-Digit", to: "/whilsco-digit" },
      { l: "FAQ", to: "/faq" },
      { l: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Légal",
    links: [
      { l: "Mentions légales", to: "/mentions-legales" },
      { l: "Politique de confidentialité", to: "/mentions-legales#confidentialite" },
      { l: "Conditions d'utilisation", to: "/mentions-legales#cgu" },
    ],
  },
];

export const Footer = () => {
  const { content } = useContent();
  const whatsapp = content?.company?.whatsapp || "";
  const email = content?.company?.email || "i.wilson@whilsco.fr";
  const siege = content?.company?.siege || "Île Maurice";
  const whatsappHref = whatsapp
    ? `https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`
    : "#";

  return (
    <footer
      data-testid={FOOTER.root}
      className="relative bg-[#0F2A1B] text-white pt-20 pb-8 noise-overlay overflow-hidden"
    >
      <div className="market-frieze absolute top-0 left-0 right-0" />

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10 pb-14 border-b border-white/10">
          <div className="col-span-2 md:col-span-3">
            <Link to="/" className="inline-flex items-center gap-3 hover:-translate-y-0.5 transition-transform">
              <ZafaraLogoMark className="h-10 w-10" />
              <div className="flex flex-col">
                <div className="flex items-baseline">
                  <span className="font-display font-bold text-2xl text-white leading-none">Za</span>
                  <span className="font-display font-bold text-2xl text-[#F5A800] leading-none">fara</span>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <span className="font-utility text-[10px] tracking-[0.2em] text-white/60 uppercase">Bénin</span>
                  <BeninFlag className="h-3 w-[18px]" />
                </div>
              </div>
            </Link>
            <p className="mt-6 text-white/60 text-sm leading-relaxed max-w-sm">
              Zafara est l'application du quotidien au Bénin. Née à Cotonou,
              conçue pour aller loin. Éditée par Whilsco-Digit.
            </p>

            <div className="mt-6 flex items-center gap-3">
              {[Facebook, Instagram, Linkedin].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label="Réseau social"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 hover:bg-[#F5A800] hover:border-[#F5A800] hover:text-[#16241B] transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {COLS.map((c) => (
            <div key={c.title}>
              <p className="font-utility text-xs uppercase tracking-widest text-white/50">{c.title}</p>
              <ul className="mt-4 space-y-3">
                {c.links.map((l) => (
                  <li key={l.l}>
                    <Link
                      to={l.to}
                      className="text-sm text-white/80 hover:text-[#F5A800] transition-colors"
                    >
                      {l.l}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <p className="font-utility text-xs uppercase tracking-widest text-white/50">Contact</p>
            <ul className="mt-4 space-y-3 text-sm text-white/80">
              <li>
                <a href={`mailto:${email}`} className="hover:text-[#F5A800] transition-colors break-all">
                  {email}
                </a>
              </li>
              <li>Siège : {siege}</li>
              {whatsapp && (
                <li>
                  WhatsApp Bénin :{" "}
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#F5A800] transition-colors"
                  >
                    {whatsapp}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white/50">
            Zafara est une marque de <span className="text-white">Whilsco-Digit</span>. © Whilsco-Digit {new Date().getFullYear()} — Fait avec fierté au Bénin.
          </p>
          <p className="text-xs text-white/40 font-utility uppercase tracking-widest">
            Local · Rapide · De confiance
          </p>
        </div>

        <div className="mt-12 -mb-4 select-none pointer-events-none">
          <p className="font-display font-bold text-[18vw] leading-none tracking-tighter">
            <span className="bg-gradient-to-b from-white/[0.08] to-transparent bg-clip-text text-transparent">
              ZAFARA
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
};
